/**
 * LinkedIn Chat Area Component
 *
 * CENTER panel showing chat messages and input.
 * Displays empty state when no connection is selected.
 */

import React, { useState } from "react";
import { Icons } from "../../../../../../views/crm-pages/conversation/constant";
import { LinkedInChatAreaProps } from "./types";
import { MessageBubble } from "./message-bubble";

export const LinkedInChatArea: React.FC<LinkedInChatAreaProps> = ({
  connection,
  messages,
  messageInput,
  onMessageChange,
  onSendMessage,
}) => {
  const [manualReplyEnabled, setManualReplyEnabled] = useState(false);

  // Empty state when no connection selected
  if (!connection) {
    return (
      <div className="flex-1 bg-[#f7f8fb] dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center px-6">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 shadow-sm">
            <Icons.Linkedin />
          </div>
          <h2 className="text-xl font-bold text-slate-800 dark:text-gray-100">LinkedIn Messages</h2>
          <p className="mt-2 text-sm text-slate-500 dark:text-gray-400 max-w-sm">
            Select a connection to start messaging. Your conversations will appear here.
          </p>
        </div>
      </div>
    );
  }

  const initials = connection.name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="flex-1 bg-[#f7f8fb] dark:bg-gray-950 flex flex-col">
      {/* Chat Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-slate-200 dark:border-gray-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            {connection.avatar ? (
              <img
                src={connection.avatar}
                alt={connection.name}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-[#1C6E46] text-white flex items-center justify-center text-sm font-semibold">
                {initials || "LI"}
              </div>
            )}
            {connection.isOnline && (
              <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white dark:border-gray-900" />
            )}
          </div>
          <div>
            <h3 className="font-semibold text-sm text-slate-800 dark:text-gray-100">{connection.name}</h3>
            <p className="text-xs text-slate-500 dark:text-gray-400">{connection.isOnline ? "Active now" : "Offline"}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button className="p-2 hover:bg-slate-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
            <Icons.Phone size={18} />
          </button>
          <button className="p-2 hover:bg-slate-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
            <Icons.Video size={18} />
          </button>
          <button className="p-2 hover:bg-slate-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
            <Icons.MoreVertical size={18} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4">
        {messages.map((message) => (
          <MessageBubble
            key={message.id}
            message={message}
            senderAvatar={!message.isOwn ? connection.avatar : undefined}
          />
        ))}
      </div>

      {/* Message Input */}
      <div className="bg-white dark:bg-gray-900 px-3.5 py-2.5 border-t border-[#e8ecf4] dark:border-gray-800">
        <div className="flex items-center gap-2 mb-2">
          <button className="bg-slate-100 dark:bg-gray-800 rounded-lg border border-slate-200 dark:border-gray-700 px-3 py-1 text-xs text-indigo-500 dark:text-indigo-400 font-semibold cursor-pointer flex items-center gap-1">
            <Icons.Sparkles /> Suggest
          </button>
          <button className="bg-slate-100 dark:bg-gray-800 rounded-lg border border-slate-200 dark:border-gray-700 px-3 py-1 text-xs text-slate-600 dark:text-gray-300 font-semibold cursor-pointer flex items-center gap-1">
            <Icons.Comment /> Templates
          </button>
          <div className="flex-1 flex items-center bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg px-3 py-1.5 gap-2">
            <input
              value={messageInput}
              onChange={(e) => onMessageChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  onSendMessage();
                }
              }}
              placeholder="Write your message..."
              className="flex-1 border-none bg-transparent outline-none text-sm text-slate-800 dark:text-gray-100 placeholder:text-slate-400 dark:placeholder:text-gray-500"
            />
            <button className="bg-transparent border-none cursor-pointer p-1.5 rounded-lg text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors duration-150 flex items-center justify-center">
              <Icons.Mic />
            </button>
            <button className="bg-transparent border-none cursor-pointer p-1.5 rounded-lg text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors duration-150 flex items-center justify-center">
              <Icons.Attach />
            </button>
          </div>
          <button
            onClick={onSendMessage}
            disabled={!messageInput.trim()}
            className="bg-indigo-500 text-white border-none rounded-lg px-4 py-2 font-bold text-sm cursor-pointer flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icons.Send />
            Send
          </button>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setManualReplyEnabled(!manualReplyEnabled)}
              className={`relative w-9 h-5 rounded-full transition-colors duration-200 cursor-pointer ${
                manualReplyEnabled ? "bg-indigo-500" : "bg-slate-300 dark:bg-gray-700"
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                  manualReplyEnabled ? "translate-x-4" : "translate-x-0"
                }`}
              />
            </button>
            <span className="text-xs text-slate-500 dark:text-gray-400">Enable Manual Reply</span>
          </div>
          <button className="flex items-center gap-1.5 bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg px-3 py-1 cursor-pointer text-xs font-semibold text-slate-600 dark:text-gray-300">
            <Icons.Agents size={14} />
            Assign To
            <Icons.ChevronDown size={13} />
          </button>
        </div>
      </div>
    </div>
  );
};
